import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface RestaurantHoverDetails {
  restaurant: {
    id: string;
    name: string;
    address: string;
    type: string;
    phone: string | null;
    website: string | null;
    googleMapsLink: string | null;
    tags: string[] | null;
    neighborhoodName: string | null;
  };
  /** The same score the matcher ranks on — see services/venueScore.ts. */
  venueScore: {
    score: number;
    prior: number;
    /** Mean of our own venue ratings; null until someone has been sent here. */
    observed: number | null;
    ratingCount: number;
    /** How much of `score` came from real survey data (0-1). */
    observedWeight: number;
    basis: string[];
    isAudited: boolean;
    auditedAt: string | null;
    auditSource: string | null;
  };
  /** Hard constraints a booker/matcher must honour, pre-formatted server-side. */
  operatingNotes: string[];
  stats: {
    avgNps: number | null;
    avgExperience: number | null;
    responseCount: number;
    ratingDistribution: {
      perfect: number;
      good: number;
      okay: number;
      notGreat: number;
      poor: number;
    };
  } | null;
  recentFeedback: {
    experience: number | null;
    rating: string | null;
    note: string | null;
    date: string | null;
    firstName: string | null;
  }[];
  totalDinners: number;
}

export function useRestaurantHoverDetails(restaurantId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'restaurant-hover', restaurantId],
    queryFn: async () => {
      const res = await api.get<RestaurantHoverDetails>(`/admin/restaurants/${restaurantId}/hover-details`);
      if (!res.data) throw new Error('No data returned');
      return res.data;
    },
    enabled: enabled && !!restaurantId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
